/**
 * 전화번호를 하이픈 없는 형태로 정규화
 * @param phoneNumber - 원본 전화번호 (예: "010-1111-1111", "01011111111")
 * @returns 정규화된 전화번호 (예: "01011111111")
 */
export const normalizePhoneNumber = (phoneNumber: string): string => {
  // null, undefined, 빈 문자열 처리
  if (!phoneNumber) return phoneNumber;
  
  // 숫자만 추출 (하이픈 등 제거)
  let cleaned = phoneNumber.replace(/\D/g, '');
  
  // 82로 시작하는 경우 (한국 국제번호)
  if (cleaned.startsWith('82')) {
    // 82 제거하고 0 추가
    cleaned = '0' + cleaned.substring(2);
  }
  
  return cleaned;
};

/**
 * 전화번호에 하이픈(-) 포맷팅 적용
 * @param phoneNumber - 원본 전화번호 (예: "01011111111", "010-1111-1111", "821092191063")
 * @returns 포맷된 전화번호 (예: "010-1111-1111")
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  // null, undefined, 빈 문자열 처리
  if (!phoneNumber) return phoneNumber;
  
  // 이미 하이픈이 포함된 경우 그대로 반환
  if (phoneNumber.includes('-')) return phoneNumber;
  
  // 숫자만 추출
  let cleaned = phoneNumber.replace(/\D/g, '');
  
  // 82로 시작하는 경우 (한국 국제번호)
  if (cleaned.startsWith('82')) {
    // 82 제거하고 0 추가
    cleaned = '0' + cleaned.substring(2);
  }
  
  // 10자리 또는 11자리 전화번호 포맷팅
  if (cleaned.length === 10) {
    // 010-111-1111 형식 (10자리)
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  } else if (cleaned.length === 11) {
    // 010-1111-1111 형식 (11자리)
    return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  }
  
  // 포맷팅이 불가능한 경우 원본 반환
  return phoneNumber;
};

/**
 * 값이 전화번호 형식인지 확인
 * @param value - 확인할 값
 * @returns 전화번호 형식 여부
 */
export const isPhoneNumber = (value: string): boolean => {
  if (!value) return false;
  
  // 숫자만 추출
  const cleaned = value.replace(/\D/g, '');
  
  // 한국 전화번호 패턴 확인
  // 010으로 시작하는 10-11자리
  if (cleaned.startsWith('010') && (cleaned.length === 10 || cleaned.length === 11)) {
    return true;
  }
  
  // 82로 시작하는 12-13자리 (국제번호)
  if (cleaned.startsWith('82') && (cleaned.length === 12 || cleaned.length === 13)) {
    return true;
  }
  
  // 기타 한국 전화번호 패턴 (011, 016, 017, 018, 019 등)
  if (/^01[1-9]\d{7,8}$/.test(cleaned)) {
    return true;
  }
  
  return false;
};

/**
 * 전화번호 또는 일반 텍스트를 적절히 포맷팅
 * @param value - 포맷할 값
 * @returns 전화번호인 경우 포맷팅, 아닌 경우 원본 반환
 */
export const formatPhoneNumberOrText = (value: string): string => {
  if (!value) return value;
  
  // 전화번호인지 확인
  if (isPhoneNumber(value)) {
    return formatPhoneNumber(value);
  }
  
  // 전화번호가 아니면 원본 그대로 반환
  return value;
};